/**
 * asset-filter-params.ts — Client-SSOT für URL→Filter-Mapping der by-filter-Mutation.
 *
 * `buildByFilterParams` zieht die Filter-Achsen aus den Library-`searchParams` und
 * baucht das `filters`-Objekt für `POST /api/assets/bulk/tags/by-filter`. Es
 * spiegelt EXAKT den GET/stats-Filterset von `LibraryPage` (queryString-Builder):
 * dieselben Achsen, dieselben Default-Auslassungen — damit der dryRun-Count nicht
 * von der im Grid gezeigten Treffermenge driftet (Roadmap Phase 1b).
 *
 * Bewusst NICHT enthalten (SELECT-Shape / Präsentation): `stacks`-Grouping,
 * `sort`, `dir`, `view`, `cols`, `cursor`. Das `stack`-Drill-in dagegen IST ein
 * WHERE-Prädikat (s. asset-where.ts) und kommt mit.
 */
import { ASSET_TYPES } from '../../shared/asset-constants';
import { parseTagFilter } from '../../shared/category-validation';
import { parseStatus } from './parse-status';

// Server-gültige Asset-Typen. `'all'` ist NUR ein Client-Filterwert — die
// Server-`AssetTypeSchema` hat KEIN `.catch`, ein `type=all` würde die Mutation
// 400en. Darum hier strikt auf die echten Typen filtern (Default-Auslassung wie
// im GET-queryString: `if (type !== 'all')`).
const VALID_SERVER_TYPES = new Set<string>(ASSET_TYPES);

/**
 * Extrahiert die Filter-Achsen aus den URL-`searchParams` als string-Record
 * (Body-`filters` der by-filter-Mutation). Leere/Default-Werte werden
 * weggelassen — identisch zur GET-Liste, damit der dryRun dieselbe Menge zählt.
 */
export function buildByFilterParams(
  searchParams: URLSearchParams,
): Record<string, string> {
  const out: Record<string, string> = {};

  // type — nur server-gültige Typen (img/video/pdf/logo); 'all'/Müll weglassen.
  const type = searchParams.get('type');
  if (type && VALID_SERVER_TYPES.has(type)) out.type = type;

  // status — nur wenn non-default. parseStatus mappt Tampering → 'approved'
  // (= Default → weglassen, wie GET: `if (status !== 'approved')`).
  const status = parseStatus(searchParams.get('status'));
  if (status !== 'approved') out.status = status;

  // q — Freitext. Getrimmt (Server trimmt ohnehin; verhindert whitespace-only
  // → min(1)-400). Identische Treffermenge wie der untrimmt sendende GET.
  const q = searchParams.get('q')?.trim();
  if (q) out.q = q;

  // match — nur der OR-Modus ist ein Modifier; 'and' ist Default (weglassen).
  if (searchParams.get('match') === 'or') out.match = 'or';

  // tag — normalisiert (split/trim/lowercase/dedupe/cap). parseTagFilter ist
  // idempotent → identisch zu tagKey im GET. Leere Liste → Param raus.
  const tag = parseTagFilter(searchParams.get('tag')).join(',');
  if (tag) out.tag = tag;

  // Taxonomie-Facetten — Roh-CSV (Server `parseUuidFilter` validiert; das
  // Server-Schema hat `.catch` → Müll wird ignoriert, kein 400).
  for (const key of ['kategorie', 'produkt', 'verwendung'] as const) {
    const v = searchParams.get(key);
    if (v) out[key] = v;
  }

  // Stapel-Drill-in (`?stack=<key>`) — WHERE-Prädikat. Getrimmt wie `drillStack`
  // in LibraryPage. `stacks` (Grouping) ist SELECT-Shape und kommt NICHT mit.
  const stack = searchParams.get('stack')?.trim();
  if (stack) out.stack = stack;

  // Schnellzugriff-Booleans (nur '1' zählt).
  if (searchParams.get('untagged') === '1') out.untagged = '1';
  if (searchParams.get('mine') === '1') out.mine = '1';

  return out;
}

/**
 * Mind. ein DISKRIMINIERENDER Filter aktiv? (= die „Alle N taggen"-Affordanz
 * wird gezeigt). Abgeleitet aus `buildByFilterParams` (SSOT): jeder produzierte
 * Key ZÄHLT, AUSSER `status` und `match` —
 *  - `status` allein ist nur die Default-Ansicht-Variante (z.B. `?status=all`),
 *    kein inhaltlicher Filter → soll NICHT „tag alle 6800" aus der Vollansicht
 *    freischalten.
 *  - `match` ist ein q-gekoppelter Such-Modus-Modifier, allein bedeutungslos.
 */
export function hasDiscriminatingFilter(searchParams: URLSearchParams): boolean {
  const params = buildByFilterParams(searchParams);
  return Object.keys(params).some((k) => k !== 'status' && k !== 'match');
}
